import _ from 'lodash';

import {
  CreditAgreement,
  FormulaEngine,
} from '@finley-technologies/finley-types';

import { WipConfig } from 'packages/creditAgreements/ui/types';

export type ThresholdType = 'min' | 'max';

export type AddThresholdValueField = {
  wc: WipConfig;
  thresholdLevel: number;
  thresholdFormula: string;
  action: 'add';
};

export type DeleteThresholdValueField = {
  wc: WipConfig;
  action: 'delete';
  indexToDelete: number;
};

export const thresholdTypeOptions: { value: ThresholdType; label: string }[] = [
  { value: 'max', label: 'Max' },
  { value: 'min', label: 'Min' },
];

export const portfolioCovenantValueField: CreditAgreement.Config.PortfolioCovenant.RequiredIntermediateField =
  'portfolioCovenantValue';
export const financialCovenantValueField: CreditAgreement.Config.FinancialCovenant.RequiredIntermediateField =
  'financialCovenantValue';

type CovenantType = 'portfolio' | 'financial';
type CovenantRequiredIntermediateField =
  | CreditAgreement.Config.PortfolioCovenant.RequiredIntermediateField
  | CreditAgreement.Config.FinancialCovenant.RequiredIntermediateField;

const getCovenantRequiredFields = (
  covenantType: CovenantType,
): {
  covenantResultField: CovenantRequiredIntermediateField;
  covenantValueField: CovenantRequiredIntermediateField;
} => {
  return {
    covenantResultField: `${covenantType}CovenantResult`,
    covenantValueField: `${covenantType}CovenantValue`,
  } as const;
};

// this can be updated if more levels are needed
export const MAX_THRESHOLD_LEVEL = 10;

export const createThresholdIntermediateFieldShell = (
  thresholdLevel: number,
  thresholdFormula: string,
): CreditAgreement.Config.CommonIntermediateField.IntermediateField => ({
  field: `LEVEL_${thresholdLevel}_THRESHOLD`,
  config: {
    formula: thresholdFormula,
    type: 'formula',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
});

export const isThresholdValueIntermediateField = (field: string): boolean => {
  const splitField = field.split('_');
  if (
    splitField.length === 3 &&
    splitField[0] === 'LEVEL' &&
    splitField[2] === 'THRESHOLD'
  ) {
    return true;
  }
  return false;
};

export const getThresholdLevel = (field: string): number | null => {
  if (isThresholdValueIntermediateField(field)) {
    const splitField = field.split('_');
    const numberLevel = _.toNumber(splitField[1]);
    return _.isNumber(numberLevel) ? numberLevel : null;
  }

  return null;
};

export const getThresholdType = (
  covenantType: CovenantType,
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[],
): ThresholdType | null => {
  const { covenantValueField, covenantResultField } = getCovenantRequiredFields(
    covenantType,
  );

  const covenantResultIntermediateField = intermediateFields.find(
    (intermediateField) => intermediateField.field === covenantResultField,
  );

  if (
    !covenantResultIntermediateField ||
    !covenantResultIntermediateField.config.formula
  ) {
    return null;
  }

  const formulaWithoutWhitespace = covenantResultIntermediateField.config.formula.replace(
    /\s/g,
    '',
  );

  const maxThresholdTypeIndicator = `${covenantValueField}<`;
  const minThresholdTypeIndicator = `${covenantValueField}>`;

  if (_.includes(formulaWithoutWhitespace, maxThresholdTypeIndicator)) {
    return 'max';
  }
  if (_.includes(formulaWithoutWhitespace, minThresholdTypeIndicator)) {
    return 'min';
  }
  return null;
};

export const getCovenantValueFormula = ({
  type,
  creditAgreementCovenant,
}: {
  type: 'portfolio' | 'financial';
  creditAgreementCovenant?:
    | CreditAgreement.DomainEntity.PortfolioCovenantProperty
    | CreditAgreement.DomainEntity.FinancialCovenantProperty;
}) => {
  return creditAgreementCovenant && creditAgreementCovenant._config_
    ? _.find(
        creditAgreementCovenant._config_.intermediateFields,
        (intermediateField) =>
          type === 'portfolio'
            ? intermediateField.field === portfolioCovenantValueField
            : intermediateField.field === financialCovenantValueField,
      )?.config.formula
    : null;
};

/**
 * Generates a static CovenantResult formula string, given configured threshold intermediate fields.
 * This formula will will be used by covenant engine to evaluate and determine the result of the covenant
 *
 * Example output string:
 * IF(CovenantValue < LEVEL_1_THRESHOLD, "PASS",
 *   IF(CovenantValue < LEVEL_2_THRESHOLD, "LEVEL_1_FAIL",
 *     IF(CovenantValue < LEVEL_3_THRESHOLD, "LEVEL_2_FAIL", "LEVEL_3_FAIL")))
 */

const _generateResultFormula = ({
  covenantType,
  orderedThresholdIntermediateFields,
  thresholdType,
}: {
  covenantType: CovenantType;
  orderedThresholdIntermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  thresholdType: ThresholdType;
}): string => {
  const { covenantValueField } = getCovenantRequiredFields(covenantType);
  const thresholdTypeSymbol = thresholdType === 'max' ? '<' : '>';

  const covenantResultFormula = _.reduce(
    orderedThresholdIntermediateFields,
    (acc, currentThresholdIntermediateField, index) => {
      const thresholdLevel = getThresholdLevel(
        currentThresholdIntermediateField.field,
      );

      if (!thresholdLevel) {
        return '';
      }

      const finalFailConditionFormula = `"LEVEL_${thresholdLevel}_FAIL"`;
      const previousThresholdIntermediateField =
        orderedThresholdIntermediateFields[index - 1];

      const baseFormula = `IF(${covenantValueField} ${thresholdTypeSymbol} LEVEL_${thresholdLevel}_THRESHOLD, ${
        index === 0
          ? '"PASS"'
          : `"LEVEL_${
              previousThresholdIntermediateField
                ? getThresholdLevel(previousThresholdIntermediateField.field)
                : thresholdLevel - 1
            }_FAIL"`
      }, `;

      // eslint-disable-next-line no-param-reassign
      acc += baseFormula;

      if (index === orderedThresholdIntermediateFields.length - 1) {
        // eslint-disable-next-line no-param-reassign
        acc += finalFailConditionFormula;
        const closingBrackets = ')'.repeat(
          orderedThresholdIntermediateFields.length,
        );
        // eslint-disable-next-line no-param-reassign
        acc += closingBrackets;
      }

      return acc;
    },
    '',
  );

  return covenantResultFormula;
};

const _createCovenantResultFormulaFromThresholds = ({
  covenantType,
  intermediateFields,
  thresholdType,
}: {
  covenantType: CovenantType;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  thresholdType: ThresholdType;
}): string => {
  const orderedIntermediateFields = _.orderBy(
    intermediateFields,
    'field',
    'asc',
  );
  const thresholdIntermediateFields = _.filter(
    orderedIntermediateFields,
    (intermediateField) =>
      isThresholdValueIntermediateField(intermediateField.field),
  );

  return _generateResultFormula({
    covenantType,
    orderedThresholdIntermediateFields: thresholdIntermediateFields,
    thresholdType,
  });
};

export const getNewIntermediateFieldsWithCovenantResultFormula = ({
  covenantType,
  thresholdType,
  intermediateFields,
}: {
  covenantType: CovenantType;
  thresholdType: ThresholdType;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
}):
  | CreditAgreement.Config.CommonIntermediateField.IntermediateField[]
  | null => {
  const { covenantResultField } = getCovenantRequiredFields(covenantType);

  const newIntermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[] = _.cloneDeep(
    intermediateFields,
  );

  // generate updated CovenantResult field
  const covenantResultIntermediateFieldIndex = newIntermediateFields.findIndex(
    (intermediateField) => intermediateField.field === covenantResultField,
  );
  const existingCovenantResultIntermediateField =
    newIntermediateFields[covenantResultIntermediateFieldIndex];

  if (!existingCovenantResultIntermediateField) {
    return null;
  }

  const newFormula = _createCovenantResultFormulaFromThresholds({
    covenantType,
    intermediateFields: newIntermediateFields,
    thresholdType,
  });
  const newCovenantResultIntermediateField = {
    ...existingCovenantResultIntermediateField,
    config: {
      ...existingCovenantResultIntermediateField.config,
      formula: newFormula,
    },
  };

  // replace existing CovenantResult intermediate field with new one
  newIntermediateFields.splice(
    covenantResultIntermediateFieldIndex,
    1,
    newCovenantResultIntermediateField,
  );

  return newIntermediateFields;
};
